import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CommonBuyerResponse } from '@portal/models/commonBuyerResponse';
import type { LegalBuyerResponse } from '@portal/models/legalBuyerResponse';
import { LegalSellerResponse } from '@portal/models/legalSellerResponse';
import { InternalCallbackService } from './internal-callback.service';

enum CallbackType {
  Buyers = 'buyers',
  Sellers = 'sellers',
}
@UntilDestroy()
@Component({
  selector: 'portal-internal-callback',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InternalCallbackComponent implements OnInit {
  private readonly internalCallbackService = inject(InternalCallbackService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(
        map(params => params.get('type')),
        switchMap((type: CallbackType) => {
          const id = this.activatedRoute.snapshot.paramMap.get('id');
          switch (type) {
            case CallbackType.Sellers:
              return this.internalCallbackService.loadSeller(+id);
            case CallbackType.Buyers:
              return this.internalCallbackService.loadBuyer(+id);
            default:
              throw new Error('Unknown type');
          }
        }),
        catchError(err => {
          this.router.navigate(['/not-found']);

          return err;
        }),
        untilDestroyed(this),
      )
      .subscribe((res: LegalSellerResponse | CommonBuyerResponse | LegalBuyerResponse): void => {
        const type: CallbackType = this.activatedRoute.snapshot.paramMap.get('type') as CallbackType;
        switch (type) {
          case CallbackType.Sellers:
            this.router.navigate(['/companies', (res as LegalSellerResponse).companyId], { replaceUrl: true });
            break;
          case CallbackType.Buyers:
            if (this.isLegalBuyer(res)) {
              this.router.navigate(['/companies', res.companyId], { replaceUrl: true });
              break;
            }

            this.router.navigate(['/users', (res as CommonBuyerResponse).user?.id], { replaceUrl: true });
            break;
          default:
            throw new Error('Unknown type');
        }
      });
  }

  private isLegalBuyer(res: LegalBuyerResponse | CommonBuyerResponse | LegalSellerResponse): res is LegalBuyerResponse {
    return (res as LegalSellerResponse).companyId !== undefined;
  }
}
